import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Pencil, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { MultiButton, MultiButtonGroup } from '@/components/ui/multi-button'
import { AdminListPage } from '../components/AdminListPage'
import { useAdminFilms } from '../hooks/useAdminFilms'
import { useAdminSeasons } from '../hooks/useAdminSeasons'
import type { FilmDocument } from '@/types'

export function AdminFilmsPage() {
  const navigate = useNavigate()
  const [seasonFilter, setSeasonFilter] = useState<number | undefined>(undefined)
  const { films, loading, error, deleteFilm } = useAdminFilms(seasonFilter)
  const { seasons } = useAdminSeasons()
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<(FilmDocument & { id: string }) | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)

  async function handleDelete() {
    if (!deleteTarget) return
    setDeletingId(deleteTarget.id)
    try {
      await deleteFilm(deleteTarget.id)
    } finally {
      setDeletingId(null)
      setDeleteTarget(null)
      setDialogOpen(false)
    }
  }

  function openDeleteDialog(film: FilmDocument & { id: string }) {
    setDeleteTarget(film)
    setDialogOpen(true)
  }

  function handleSeasonFilterChange(value: string) {
    if (value === 'all') {
      setSeasonFilter(undefined)
    } else {
      setSeasonFilter(Number(value))
    }
  }

  return (
    <AdminListPage
      title="Films"
      action={
        <Button asChild>
          <Link to="/admin/films/new">Add Film</Link>
        </Button>
      }
      filters={
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-muted-foreground">Filter by season:</span>
          <Select
            value={seasonFilter !== undefined ? String(seasonFilter) : 'all'}
            onValueChange={handleSeasonFilterChange}
          >
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All seasons" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All seasons</SelectItem>
              {seasons.map((s) => (
                <SelectItem key={s.id} value={String(s.number)}>
                  {s.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      }
      error={error}
      loading={loading}
      dialog={
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger className="hidden" />
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Film</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              Are you sure you want to delete{' '}
              <span className="font-semibold text-foreground">{deleteTarget?.title}</span>? This
              action cannot be undone.
            </p>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setDialogOpen(false)
                  setDeleteTarget(null)
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => void handleDelete()}
                disabled={deletingId !== null}
              >
                {deletingId !== null ? 'Deleting…' : 'Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      }
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Title</TableHead>
            <TableHead>Season</TableHead>
            <TableHead>Episode</TableHead>
            <TableHead>Published</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <MultiButtonGroup>
          <TableBody>
            {films.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                  No films found. Add one to get started.
                </TableCell>
              </TableRow>
            ) : (
              films.map((film) => (
                <TableRow key={film.id}>
                  <TableCell className="font-medium">{film.title}</TableCell>
                  <TableCell>Season {film.seasonNumber}</TableCell>
                  <TableCell>Ep. {film.episodeNumber}</TableCell>
                  <TableCell>
                    <Badge variant={film.isPublished ? 'default' : 'secondary'}>
                      {film.isPublished ? 'Published' : 'Draft'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end">
                      <MultiButton
                        variant="outline"
                        size="sm"
                        items={[
                          { id: 'edit', icon: Pencil, label: 'Edit', onClick: () => navigate(`/admin/films/${film.id}/edit`) },
                          { id: 'delete', icon: Trash2, label: 'Delete', onClick: () => openDeleteDialog(film), disabled: deletingId === film.id, className: 'text-destructive hover:text-destructive' },
                        ]}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </MultiButtonGroup>
      </Table>
    </AdminListPage>
  )
}
